import type { WarehouseRes } from 'api/swagger';
import { useGetManyWarehouses } from 'api/warehouses/use-get-many-warehouses';
import type { ICargoFilter } from 'shared/stores/use-cargo-filters';
import { useWhInfoFiltersStore } from 'shared/stores/use-wh-info-filters';
import { findWarehouseById } from 'shared/utils/warehouses/find-warehouse-by-id';
import { CARGO_STATUS_LOADED, CARGO_STATUS_PLANNED, CARGO_STATUS_WAREHOUSE } from 'types/cargo';
import { WarehouseName } from 'components/wh-info/warehouse-name';
import { WhInfoCargoesGroup } from '../wh-info-cargoes-group';
import { WhInfoCargoesTotals } from '../wh-info-cargoes-totals';
import { WhInfoColumn } from '../wh-info-column';
import styles from './wh-info-cargoes-content.module.scss';

export const WhInfoCargoesContent = () => {
  const { filters } = useWhInfoFiltersStore();
  const { data: warehouses } = useGetManyWarehouses();
  const { warehouseId, importExportType } = filters || {};
  const warehouse = findWarehouseById(warehouseId || null, warehouses) as WarehouseRes;

  const warehouseName = warehouse?.name;
  const warehouseCountryId = warehouse?.countryId;

  const getBaseFilters = (() => {
    const baseFilters: ICargoFilter = {};

    if (importExportType === 'import' && warehouseCountryId) {
      baseFilters.unloadingPlaceCountryId = [warehouseCountryId];
    } else if (importExportType === 'export' && warehouseCountryId) {
      baseFilters.loadingPlaceCountryId = [warehouseCountryId];
    }

    return baseFilters;
  })();

  if (!warehouseId) {
    return null;
  }

  return (
    <>
      <WhInfoColumn title={<WarehouseName warehouseId={warehouseId} prefix='To ' />}>
        <div className={styles.totals}>
          <WhInfoCargoesTotals
            title='Loaded total'
            filters={{
              ...getBaseFilters,
              cargoStatuses: [`${CARGO_STATUS_LOADED} to ${warehouseName}`],
            }}
          />
          <WhInfoCargoesTotals
            title='Planned total'
            filters={{
              ...getBaseFilters,
              cargoStatuses: [`${CARGO_STATUS_PLANNED} to ${warehouseName}`],
            }}
          />
        </div>
        <WhInfoCargoesGroup
          title='Loaded cargoes'
          filters={{
            ...getBaseFilters,
            cargoStatuses: [`${CARGO_STATUS_LOADED} to ${warehouseName}`],
          }}
        />
        <WhInfoCargoesGroup
          title='Planned cargoes'
          filters={{
            ...getBaseFilters,
            cargoStatuses: [`${CARGO_STATUS_PLANNED} to ${warehouseName}`],
          }}
        />
      </WhInfoColumn>
      <WhInfoColumn title={<WarehouseName warehouseId={warehouseId} />}>
        <div className={styles.totals}>
          <WhInfoCargoesTotals
            title='Planned total'
            filters={{
              ...getBaseFilters,
              cargoStatuses: [`${CARGO_STATUS_PLANNED} from ${warehouseName}`],
            }}
          />
          <WhInfoCargoesTotals
            title='In warehouse total'
            filters={{
              ...getBaseFilters,
              cargoStatuses: [`${CARGO_STATUS_WAREHOUSE} in ${warehouseName}`],
            }}
          />
        </div>
        <WhInfoCargoesGroup
          title='Planned cargoes'
          filters={{
            ...getBaseFilters,
            cargoStatuses: [`${CARGO_STATUS_PLANNED} from ${warehouseName}`],
          }}
        />
        <WhInfoCargoesGroup
          title='In warehouse'
          filters={{
            ...getBaseFilters,
            cargoStatuses: [`${CARGO_STATUS_WAREHOUSE} in ${warehouseName}`],
          }}
        />
      </WhInfoColumn>

      <WhInfoColumn title={<WarehouseName warehouseId={warehouseId} prefix='From ' />}>
        <div className={styles.totals}>
          <WhInfoCargoesTotals
            title='Loaded total'
            filters={{
              ...getBaseFilters,
              cargoStatuses: [`${CARGO_STATUS_LOADED} from ${warehouseName}`],
            }}
          />
        </div>
        <WhInfoCargoesGroup
          title='Loaded cargoes'
          filters={{
            ...getBaseFilters,
            cargoStatuses: [`${CARGO_STATUS_LOADED} from ${warehouseName}`],
          }}
        />
      </WhInfoColumn>
    </>
  );
};
